import { useState, useEffect } from 'react';
import { api } from '../api/client';

export interface Flashcard {
  id: string;
  front: string;
  back: string;
  category: string;
  interval_days: number;
  next_review_date: string;
  review_count: number;
  last_rating?: 'again' | 'hard' | 'good' | 'easy';
  created_at: string;
}

export function TrainingPage() {
  const [cards, setCards] = useState<Flashcard[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'review' | 'manage' | 'generate'>('review');
  
  // Review Mode state
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [reviewFilter, setReviewFilter] = useState<'due' | 'all'>('due');

  // Manual Add state
  const [newFront, setNewFront] = useState('');
  const [newBack, setNewBack] = useState('');
  const [newCategory, setNewCategory] = useState('Chung');

  // AI Generator state
  const [genTopic, setGenTopic] = useState('');
  const [genCount, setGenCount] = useState(5);
  const [generating, setGenerating] = useState(false);

  const todayStr = new Date().toISOString().split('T')[0]!;

  const loadCards = async () => {
    setLoading(true);
    try {
      const res = await api.getFlashcards();
      const loaded: Flashcard[] = Array.isArray(res.cards) ? res.cards : [];
      setCards(loaded);
    } catch (e) {
      console.error('Failed to load flashcards', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCards();
  }, []);

  // Filter cards due today
  const dueCards = cards.filter(c => c.next_review_date <= todayStr);
  const reviewDeck = reviewFilter === 'due' ? dueCards : cards;
  const currentCard = reviewDeck[currentIndex];

  const handleReviewRating = async (rating: 'again' | 'hard' | 'good' | 'easy') => {
    if (!currentCard) return;

    let nextDays = 1;
    if (rating === 'again') nextDays = 1;
    else if (rating === 'hard') nextDays = Math.max(2, currentCard.interval_days * 1.2);
    else if (rating === 'good') nextDays = Math.max(3, currentCard.interval_days * 2.0);
    else if (rating === 'easy') nextDays = Math.max(5, currentCard.interval_days * 3.0);

    const nextDate = new Date();
    nextDate.setDate(nextDate.getDate() + Math.round(nextDays));
    const nextDateStr = nextDate.toISOString().split('T')[0]!;

    const updatedCard: Flashcard = {
      ...currentCard,
      interval_days: Math.round(nextDays),
      next_review_date: nextDateStr,
      review_count: (currentCard.review_count || 0) + 1,
      last_rating: rating
    };

    const updatedList = cards.map(c => c.id === currentCard.id ? updatedCard : c);
    setCards(updatedList);
    await api.saveFlashcards(updatedList).catch(e => console.error(e));

    setIsFlipped(false);
    if (currentIndex < reviewDeck.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      setCurrentIndex(0);
    }
  };

  const handleCreateManual = async () => {
    if (!newFront.trim() || !newBack.trim()) return;
    const newCard: Flashcard = {
      id: `fc_${Date.now()}`,
      front: newFront.trim(),
      back: newBack.trim(),
      category: newCategory.trim() || 'Chung',
      interval_days: 1,
      next_review_date: todayStr,
      review_count: 0,
      created_at: new Date().toISOString()
    };

    const nextList = [newCard, ...cards];
    setCards(nextList);
    await api.saveFlashcards(nextList).catch(e => console.error(e));
    setNewFront('');
    setNewBack('');
    alert('Đã thêm thẻ mới thành công!');
  };

  const handleDeleteCard = async (id: string) => {
    if (!confirm('Xóa flashcard này?')) return;
    const nextList = cards.filter(c => c.id !== id);
    setCards(nextList);
    await api.saveFlashcards(nextList).catch(e => console.error(e));
  };

  const handleAiGenerate = async () => {
    if (!genTopic.trim() || generating) return;
    setGenerating(true);
    try {
      const prompt = `Hãy đóng vai Giảng viên Huấn luyện Trí nhớ Siêu đẳng. 
Dựa vào chủ đề hoặc nội dung sau:
"${genTopic}"

Hãy tạo đúng ${genCount} Flashcards câu hỏi và câu trả lời chất lượng cao, định dạng CHÍNH XÁC theo mẫu JSON sau (không kèm markdown ngoài chuỗi json):
[
  { "front": "Câu hỏi ngắn gọn, gợi mở tư duy?", "back": "Câu trả lời cô đọng, chính xác, kèm 1 ví dụ nếu có.", "category": "Tên chủ đề" }
]`;

      const res = await api.query(prompt);
      const raw = res.context || '';
      const jsonMatch = raw.match(/\[\s*\{[\s\S]*\}\s*\]/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        const newCards: Flashcard[] = parsed.map((item: any, idx: number) => ({
          id: `fc_gen_${Date.now()}_${idx}`,
          front: item.front || 'Câu hỏi',
          back: item.back || 'Câu trả lời',
          category: item.category || 'AI Generated',
          interval_days: 1,
          next_review_date: todayStr,
          review_count: 0,
          created_at: new Date().toISOString()
        }));

        const nextList = [...newCards, ...cards];
        setCards(nextList);
        await api.saveFlashcards(nextList);
        setGenTopic('');
        setActiveTab('review');
        alert(`Đã tạo thành công ${newCards.length} Flashcards bằng AI!`);
      } else {
        alert('AI không trả về đúng định dạng JSON flashcard. Vui lòng thử lại.');
      }
    } catch (e: any) {
      alert('Lỗi sinh flashcards: ' + (e.message || 'Unknown error'));
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '16px', flexDirection: 'column' }}>
        <div className="spinner" style={{ width: 32, height: 32 }} />
        <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Đang tải Flashcard Trainer...</span>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '0' }}>

      {/* Page Header */}
      <div className="page-header" style={{ flexShrink: 0 }}>
        <div>
          <h1 className="page-title">🧠 Flashcard Trainer &amp; Spaced Repetition</h1>
          <p className="page-subtitle">
            Ôn tập ngắt quãng thông minh (.ai/training/) giúp ghi nhớ vĩnh viễn kiến thức, thuật ngữ và tài liệu cốt lõi
          </p>
        </div>

        {/* Tab Controls */}
        <div style={{ display: 'flex', background: 'var(--bg-glass)', borderRadius: '8px', padding: '3px', border: '1px solid var(--border)' }}>
          <button
            className={`btn btn--sm ${activeTab === 'review' ? 'btn--primary' : 'btn--ghost'}`}
            onClick={() => { setActiveTab('review'); setIsFlipped(false); }}
          >
            🎯 Ôn Tập ({dueCards.length} thẻ đến hạn)
          </button>
          <button
            className={`btn btn--sm ${activeTab === 'generate' ? 'btn--primary' : 'btn--ghost'}`}
            onClick={() => setActiveTab('generate')}
          >
            ✨ AI Tự Động Sinh Thẻ
          </button>
          <button
            className={`btn btn--sm ${activeTab === 'manage' ? 'btn--primary' : 'btn--ghost'}`}
            onClick={() => setActiveTab('manage')}
          >
            📋 Quản Lý ({cards.length})
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>

        {/* TAB 1: REVIEW MODE */}
        {activeTab === 'review' && (
          <div style={{ maxWidth: '650px', width: '100%', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '20px', padding: '20px 0' }}>
            
            {/* Filter Pill */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                {reviewDeck.length > 0 ? `Thẻ ${currentIndex + 1} / ${reviewDeck.length}` : '0 thẻ'}
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button
                  className="btn btn--sm"
                  style={{
                    background: reviewFilter === 'due' ? 'rgba(6,182,212,0.2)' : 'transparent',
                    borderColor: reviewFilter === 'due' ? 'var(--accent-cyan)' : 'var(--border)',
                    fontSize: '11px'
                  }}
                  onClick={() => { setReviewFilter('due'); setCurrentIndex(0); setIsFlipped(false); }}
                >
                  ⚡ Cần ôn ({dueCards.length})
                </button>
                <button
                  className="btn btn--sm"
                  style={{
                    background: reviewFilter === 'all' ? 'rgba(124,58,237,0.2)' : 'transparent',
                    borderColor: reviewFilter === 'all' ? 'var(--accent-primary)' : 'var(--border)',
                    fontSize: '11px'
                  }}
                  onClick={() => { setReviewFilter('all'); setCurrentIndex(0); setIsFlipped(false); }}
                >
                  📚 Toàn bộ ({cards.length})
                </button>
              </div>
            </div>

            {/* Flashcard Box */}
            {reviewDeck.length === 0 ? (
              <div className="card" style={{ textAlign: 'center', padding: '50px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px' }}>
                <span style={{ fontSize: '48px' }}>🎉</span>
                <div style={{ fontSize: '16px', fontWeight: 600 }}>Bạn đã hoàn thành bài ôn tập hôm nay!</div>
                <div style={{ fontSize: '13px', color: 'var(--text-muted)', maxWidth: '400px' }}>
                  Không còn thẻ nào đến hạn trong hôm nay. Bạn có thể bấm "Toàn bộ" để ôn luyện thêm hoặc dùng AI sinh thêm thẻ mới.
                </div>
                <button className="btn btn--primary" onClick={() => setActiveTab('generate')}>
                  ✨ Sinh thêm Flashcard bằng AI
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div
                  onClick={() => setIsFlipped(!isFlipped)}
                  style={{
                    minHeight: '260px',
                    background: isFlipped ? 'linear-gradient(135deg, #151833 0%, #1e1b4b 100%)' : 'linear-gradient(135deg, #0e1322 0%, #172033 100%)',
                    border: '1px solid',
                    borderColor: isFlipped ? 'rgba(124,58,237,0.5)' : 'rgba(6,182,212,0.4)',
                    borderRadius: '16px',
                    padding: '30px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                    transition: 'all 0.3s ease',
                    position: 'relative'
                  }}
                >
                  {/* Category Tag */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span className="badge badge--blue" style={{ fontSize: '11px' }}>
                      🏷️ {currentCard.category}
                    </span>
                    <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                      {isFlipped ? '🟢 Đáp án / Lời giải' : '🔵 Câu hỏi / Thử thách'}
                    </span>
                  </div>

                  {/* Card Content */}
                  <div style={{
                    fontSize: isFlipped ? '16px' : '18px',
                    fontWeight: isFlipped ? 500 : 700,
                    lineHeight: 1.7,
                    color: '#ffffff',
                    textAlign: 'center',
                    padding: '20px 10px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}>
                    {isFlipped ? currentCard.back : currentCard.front}
                  </div>

                  <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', textAlign: 'center', fontStyle: 'italic' }}>
                    {isFlipped ? 'Đánh giá mức độ ghi nhớ của bạn ở các nút bên dưới:' : 'Nhấn chuột vào thẻ để xem đáp án ↷'}
                  </div>
                </div>

                {/* Rating Actions */}
                {isFlipped ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
                    <button
                      className="btn"
                      onClick={() => handleReviewRating('again')}
                      style={{ background: 'rgba(239,68,68,0.15)', borderColor: '#ef4444', color: '#f87171', flexDirection: 'column', padding: '10px 4px' }}
                    >
                      <div style={{ fontWeight: 700, fontSize: '13px' }}>🔄 Quên</div>
                      <div style={{ fontSize: '10px', opacity: 0.8 }}>Ôn lại sau 1 ngày</div>
                    </button>
                    <button
                      className="btn"
                      onClick={() => handleReviewRating('hard')}
                      style={{ background: 'rgba(245,158,11,0.15)', borderColor: '#f59e0b', color: '#fbbf24', flexDirection: 'column', padding: '10px 4px' }}
                    >
                      <div style={{ fontWeight: 700, fontSize: '13px' }}>⚡ Khó</div>
                      <div style={{ fontSize: '10px', opacity: 0.8 }}>Sau 2 ngày</div>
                    </button>
                    <button
                      className="btn"
                      onClick={() => handleReviewRating('good')}
                      style={{ background: 'rgba(16,185,129,0.15)', borderColor: '#10b981', color: '#34d399', flexDirection: 'column', padding: '10px 4px' }}
                    >
                      <div style={{ fontWeight: 700, fontSize: '13px' }}>👍 Tốt</div>
                      <div style={{ fontSize: '10px', opacity: 0.8 }}>Sau 4 ngày</div>
                    </button>
                    <button
                      className="btn"
                      onClick={() => handleReviewRating('easy')}
                      style={{ background: 'rgba(59,130,246,0.15)', borderColor: '#3b82f6', color: '#60a5fa', flexDirection: 'column', padding: '10px 4px' }}
                    >
                      <div style={{ fontWeight: 700, fontSize: '13px' }}>🚀 Dễ</div>
                      <div style={{ fontSize: '10px', opacity: 0.8 }}>Sau 7 ngày</div>
                    </button>
                  </div>
                ) : (
                  <button
                    className="btn btn--primary"
                    onClick={() => setIsFlipped(true)}
                    style={{ padding: '12px', fontSize: '14px', borderRadius: '10px', justifyContent: 'center' }}
                  >
                    Xem Đáp Án (Lật Thẻ)
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* TAB 2: AI GENERATOR */}
        {activeTab === 'generate' && (
          <div style={{ maxWidth: '650px', width: '100%', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '20px', padding: '20px 0' }}>
            <div className="card">
              <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '6px', color: '#c084fc', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>✨</span> Trích Xuất &amp; Tự Động Sinh Flashcard Bằng AI
              </div>
              <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '16px' }}>
                Dán một đoạn văn bản tài liệu, bài học hoặc nhập chủ đề bạn muốn rèn luyện. AI sẽ đối soát và tạo bộ thẻ thông minh.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div>
                  <label style={{ fontSize: '12.5px', fontWeight: 600, display: 'block', marginBottom: '6px' }}>Nội dung hoặc chủ đề kiến thức:</label>
                  <textarea
                    className="textarea"
                    rows={6}
                    value={genTopic}
                    onChange={e => setGenTopic(e.target.value)}
                    placeholder="Ví dụ: Dán tóm tắt quy trình KYC ngân hàng, các điều khoản hợp đồng quan trọng, hoặc kiến thức về System Architecture..."
                    style={{ fontSize: '13px', lineHeight: 1.6 }}
                  />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Số lượng thẻ:</label>
                    <select
                      className="input"
                      value={genCount}
                      onChange={e => setGenCount(parseInt(e.target.value) || 5)}
                      style={{ width: '100px' }}
                    >
                      <option value={3}>3 thẻ</option>
                      <option value={5}>5 thẻ</option>
                      <option value={10}>10 thẻ</option>
                    </select>
                  </div>

                  <button
                    className="btn btn--primary"
                    onClick={handleAiGenerate}
                    disabled={generating || !genTopic.trim()}
                    style={{ marginTop: 'auto', padding: '9px 20px', flex: 1, justifyContent: 'center' }}
                  >
                    {generating ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Đang suy luận &amp; sinh câu hỏi...</> : '🚀 Bắt đầu Sinh Flashcards'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 3: MANAGE CARDS */}
        {activeTab === 'manage' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', padding: '20px 0' }}>
            
            {/* Quick Add Form */}
            <div className="card">
              <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '12px' }}>➕ Thêm Thẻ Thủ Công</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 140px auto', gap: '10px', alignItems: 'flex-end' }}>
                <div>
                  <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Mặt trước (Câu hỏi):</label>
                  <input
                    className="input"
                    value={newFront}
                    onChange={e => setNewFront(e.target.value)}
                    placeholder="Câu hỏi / Thuật ngữ..."
                    style={{ width: '100%' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Mặt sau (Đáp án):</label>
                  <input
                    className="input"
                    value={newBack}
                    onChange={e => setNewBack(e.target.value)}
                    placeholder="Giải nghĩa / Đáp án..."
                    style={{ width: '100%' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Chủ đề:</label>
                  <input
                    className="input"
                    value={newCategory}
                    onChange={e => setNewCategory(e.target.value)}
                    placeholder="Chung"
                    style={{ width: '100%' }}
                  />
                </div>
                <button className="btn btn--primary" onClick={handleCreateManual}>
                  + Thêm
                </button>
              </div>
            </div>

            {/* List of Cards */}
            <div className="card">
              <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '14px' }}>
                Danh Sách Flashcards Đang Quản Lý ({cards.length})
              </div>

              {cards.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-muted)' }}>
                  Chưa có thẻ nào trong hệ thống.
                </div>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th style={{ width: '35%' }}>Mặt trước (Câu hỏi)</th>
                        <th style={{ width: '35%' }}>Mặt sau (Đáp án)</th>
                        <th>Chủ đề</th>
                        <th>Kỳ hạn</th>
                        <th>Lần ôn</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {cards.map(c => (
                        <tr key={c.id}>
                          <td style={{ fontWeight: 600, fontSize: '13px' }}>{c.front}</td>
                          <td style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>{c.back}</td>
                          <td><span className="badge badge--blue">{c.category}</span></td>
                          <td style={{ fontSize: '12px' }}>{c.next_review_date} ({c.interval_days}d)</td>
                          <td style={{ fontSize: '12px' }}>{c.review_count || 0}</td>
                          <td>
                            <button className="btn btn--danger btn--sm" onClick={() => handleDeleteCard(c.id)}>
                              🗑
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

          </div>
        )}

      </div>
    </div>
  );
}
